<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'

const props = defineProps({
    open:     { type: Boolean, required: true },
    type:     { type: String,  required: true },
    total:    { type: Number,  required: true },
    fetchUrl: { type: String,  required: true },
    eyebrow:  { type: String,  default: '' },
    title:    { type: String,  required: true },
    accent:   { type: String,  default: '#FCD34D' },
    pageSize: { type: Number,  default: 6 },
    columns:  { type: Number,  default: 3 },
    extraParams: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['update:open'])

const loadedItems = ref([])
const currentPage = ref(0)
const loading     = ref(false)
const hasMore     = ref(true)
const sentinel    = ref(null)
let observer      = null

async function fetchPage(page) {
    loading.value = true
    try {
        const params = new URLSearchParams({ type: props.type, page, per_page: props.pageSize, ...props.extraParams })
        const url = `${props.fetchUrl}?${params.toString()}`
        const res  = await fetch(url, { headers: { Accept: 'application/json' } })
        const data = await res.json()
        loadedItems.value.push(...data.items)
        hasMore.value     = data.hasMore
        currentPage.value = page
    } finally {
        loading.value = false
    }
}

function close() {
    emit('update:open', false)
}

async function loadMore() {
    if (loading.value || !hasMore.value) { return }
    await fetchPage(currentPage.value + 1)
}

function setupObserver() {
    if (observer) { observer.disconnect() }
    observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { loadMore() }
    }, { threshold: 0.1 })
    if (sentinel.value) { observer.observe(sentinel.value) }
}

function teardownObserver() {
    if (observer) { observer.disconnect(); observer = null }
}

watch(() => props.open, async (open) => {
    if (open) {
        loadedItems.value = []
        currentPage.value = 0
        hasMore.value     = true
        document.body.style.overflow = 'hidden'
        await fetchPage(1)
        setupObserver()
    } else {
        teardownObserver()
        document.body.style.overflow = ''
    }
}, { immediate: true })

watch(sentinel, (el) => {
    if (el && props.open) { setupObserver() }
})

function onKeydown(e) {
    if (e.key === 'Escape' && props.open) { close() }
}

onBeforeUnmount(() => {
    teardownObserver()
    document.removeEventListener('keydown', onKeydown)
    document.body.style.overflow = ''
})

if (typeof document !== 'undefined') {
    document.addEventListener('keydown', onKeydown)
}
</script>

<template>
    <Teleport to="body">
        <div v-if="open" class="rmodal-overlay" @click.self="close">
            <div class="rmodal" role="dialog" aria-modal="true" :style="{ '--rmodal-accent': accent }">
                <div class="rmodal__head">
                    <div>
                        <div v-if="eyebrow" class="rmodal__eyebrow" :style="{ color: accent }">{{ eyebrow }}</div>
                        <h3 class="rmodal__title">{{ title }}</h3>
                    </div>
                    <button class="rmodal__close" @click="close" aria-label="Close">&#x2715;</button>
                </div>
                <div class="rmodal__body">
                    <!-- Skeleton on initial load -->
                    <div v-if="loading && loadedItems.length === 0" class="rmodal__skeleton-grid" :class="`rmodal__skeleton-grid--${columns}`">
                        <div v-for="n in pageSize" :key="n" class="skel-card">
                            <div class="skel-block skel-block--cover" />
                            <div class="skel-body">
                                <div class="skel-block skel-block--line" style="width:60%" />
                                <div class="skel-block skel-block--line" style="width:40%;margin-top:6px" />
                                <div class="skel-block skel-block--bar" />
                                <div class="skel-block skel-block--btn" />
                            </div>
                        </div>
                    </div>
                    <!-- Loaded items -->
                    <slot v-else :visible-items="loadedItems" />
                    <!-- Sentinel / load-more trigger -->
                    <div ref="sentinel" class="rmodal__sentinel">
                        <div v-if="loading && loadedItems.length > 0" class="rmodal__spinner" />
                        <div v-else-if="!hasMore && loadedItems.length > 0" class="rmodal__end">All {{ loadedItems.length }} shown</div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style>
/* ─── Shop All Modal (global — teleported to body) ─── */
.rmodal-overlay {
    position: fixed; inset: 0; z-index: 9000;
    background: rgba(0, 0, 0, 0.80);
    backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center;
    padding: 24px;
}
.rmodal {
    --bg: #111120;
    --fg: #fff;
    --mute: rgba(255,255,255,0.68);
    --mute-2: rgba(255,255,255,0.55);
    --line: rgba(255,255,255,0.13);
    --card: rgba(255,255,255,0.07);
    --gold: #FCD34D;
    --blue: #4D94FF;
    --rose: #FB7185;
    --violet: #C4B5FD;
    --emerald: #34D399;
    --rmodal-accent: #FCD34D;

    background: #16162a;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-top: 2px solid var(--rmodal-accent);
    border-radius: 24px;
    width: 100%; max-width: 1120px;
    max-height: 90vh;
    display: flex; flex-direction: column;
    box-shadow: 0 24px 64px rgba(0,0,0,0.60);
    color: #fff;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}
.rmodal__head {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 28px 36px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    flex-shrink: 0;
}
.rmodal__eyebrow {
    font-weight: 700; font-size: 11px;
    text-transform: uppercase; letter-spacing: 0.10em;
    margin-bottom: 6px;
}
.rmodal__title {
    font-family: 'Manrope', sans-serif;
    font-size: 24px; font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0; color: #fff;
}
.rmodal__close {
    background: none; border: none;
    color: #eaeaea;
    font-size: 20px; cursor: pointer;
    padding: 6px 12px; border-radius: 8px;
    line-height: 1; flex-shrink: 0;
    transition: color 0.15s, background 0.15s;
}
.rmodal__close:hover { color: #fff; background: rgba(255,255,255,0.08); }
.rmodal__body {
    overflow-y: auto;
    padding: 32px 36px;
    flex: 1;
    min-height: 0;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.22) transparent;
}
.rmodal__body::-webkit-scrollbar { width: 8px; }
.rmodal__body::-webkit-scrollbar-track { background: transparent; }
.rmodal__body::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.22);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: padding-box;
}
.rmodal__body::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.38); background-clip: padding-box; }
.rmodal__loading {
    font-size: 13px;
    color: #eaeaea;
}
/* ─── Skeleton ─── */
@keyframes skel-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
}
.rmodal__skeleton-grid {
    display: grid;
    gap: 20px;
    grid-template-columns: repeat(3, 1fr);
}
.rmodal__skeleton-grid--4 { grid-template-columns: repeat(4, 1fr); }
.rmodal__skeleton-grid--5 { grid-template-columns: repeat(5, 1fr); }
@media (max-width: 768px) {
    .rmodal__skeleton-grid,
    .rmodal__skeleton-grid--4,
    .rmodal__skeleton-grid--5 { grid-template-columns: repeat(2, 1fr); }
}
.skel-card {
    border-radius: 14px;
    overflow: hidden;
    background: rgba(255,255,255,0.05);
    animation: skel-pulse 1.6s ease-in-out infinite;
}
.skel-block {
    background: rgba(255,255,255,0.10);
    border-radius: 6px;
}
.skel-block--cover  { height: 150px; border-radius: 0; }
.skel-body          { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
.skel-block--line   { height: 12px; }
.skel-block--bar    { height: 6px; border-radius: 99px; width: 100%; }
.skel-block--btn    { height: 36px; border-radius: 10px; margin-top: 4px; }
.rmodal__spinner {
    width: 20px; height: 20px;
    border: 2px solid rgba(255,255,255,0.15);
    border-top-color: rgba(255,255,255,0.6);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.rmodal__sentinel {
    display: flex; justify-content: center;
    padding: 20px 0 4px;
    min-height: 40px;
}
.rmodal__end {
    text-align: center;
    color: #eaeaea;
    font-size: 13px;
}
@media (max-width: 768px) {
    .rmodal-overlay { padding: 0; align-items: flex-end; }
    .rmodal { border-radius: 20px 20px 0 0; max-height: 92vh; }
    .rmodal__head { padding: 20px 20px; }
    .rmodal__body { padding: 20px; }
}
</style>
